/*
  viral: <viral-lottie> plays a decorative Lottie animation with a transparent background.
  - Nothing is downloaded until the element is near the viewport (IntersectionObserver).
  - The player (assets/viral-lottie-player.js, lottie-web light, MIT) and the animation data
    (assets/viral-lottie-<name>.js) are theme assets: no third-party request.
  - Plays only while on screen; with prefers-reduced-motion it shows one still frame.
  - Survives Dawn's cart re-renders: a replaced node is destroyed and the new one starts again.
*/
(() => {
  if (customElements.get('viral-lottie')) return;

  const scripts = {};
  const loadScript = (src) => {
    if (!scripts[src]) {
      scripts[src] = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = () => {
          delete scripts[src];
          reject(new Error(`viral-lottie: could not load ${src}`));
        };
        document.head.appendChild(script);
      });
    }
    return scripts[src];
  };

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ViralLottie extends HTMLElement {
    connectedCallback() {
      if (this.observer) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.visible = entry.isIntersecting;
            if (this.visible) {
              this.start();
            } else if (this.animation) {
              this.animation.pause();
            }
          });
        },
        { rootMargin: '200px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      this.observer = null;
      if (this.animation) this.animation.destroy();
      this.animation = null;
    }

    async start() {
      if (this.animation) {
        if (!reducedMotion.matches) this.animation.play();
        return;
      }
      if (this.loading) return;
      this.loading = true;
      try {
        await loadScript(this.dataset.player);
        await loadScript(this.dataset.src);
        const source = window.viralLottieData && window.viralLottieData[this.dataset.name];
        if (!source || !window.lottie || !this.isConnected) return;

        this.animation = window.lottie.loadAnimation({
          container: this,
          renderer: 'svg',
          loop: true,
          autoplay: this.visible && !reducedMotion.matches,
          // lottie-web mutates the data it is given, so every instance gets its own copy
          animationData: JSON.parse(JSON.stringify(source)),
          rendererSettings: { progressiveLoad: true },
        });

        if (reducedMotion.matches) {
          this.animation.addEventListener('DOMLoaded', () => {
            if (this.animation) this.animation.goToAndStop(Math.floor(this.animation.totalFrames * 0.6), true);
          });
        }
      } catch (error) {
        // Decorative only: if anything fails the page simply shows no animation.
      } finally {
        this.loading = false;
      }
    }
  }

  customElements.define('viral-lottie', ViralLottie);
})();
